import { flow, toGenerator, types } from 'mobx-state-tree'

import { FetchModel } from '../models/FetchModel'
import {
  OperationFailure,
  OperationInProgress,
  OperationNotStarted,
  OperationStatusModel,
  OperationSuccess,
} from '../models/common/OperationStatus'
import { oktaAuth, OKTA_REDIRECT_URL } from '../oktaAuth'
import { loginLink } from '../router'

export const LoginStore = FetchModel.named('UserLoginStore')
  .props({
    loginStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    logOutStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
  })
  .views((self) => ({
    get oktaLoginUrl(): string {
      return `${self.baseUrl}users/okta_callback/`
    },
    get logoutUrl(): string {
      return `${self.baseUrl}users/logout/`
    },
  }))
  .actions((_self) => ({
    _logoutFromOkta: flow(function* _logoutFromOkta() {
      const isLoggedInOkta = yield* toGenerator(oktaAuth.session.exists())
      if (isLoggedInOkta) {
        yield* toGenerator(oktaAuth.signOut())
      }
    }),
  }))
  .actions((self) => ({
    loginWithAuthorizationCode: flow(function* loginWithAuthorizationCode(authorization_code: string) {
      self.loginStatus = OperationInProgress()

      const loginInfo = {
        code: authorization_code,
        redirect_uri: OKTA_REDIRECT_URL,
      }

      const fetchResult = yield* toGenerator(
        self.loadObjectOrError<void>(
          self.oktaLoginUrl,
          { method: 'POST', body: JSON.stringify(loginInfo) },
          { fetchOperationName: 'Login user with authorization code' },
        ),
      )

      if (fetchResult.error) {
        self.loginStatus = OperationFailure(fetchResult.error)
      } else {
        self.loginStatus = OperationSuccess()

        // Since center app might be used on shared device,
        // we wish to stay signed in to Okta for the shortest time possible.
        void self._logoutFromOkta()
      }
    }),
    logOut: flow(function* logout() {
      self.logOutStatus = OperationInProgress()

      const fetchResult = yield* toGenerator(
        self.loadObjectOrError<void>(self.logoutUrl, { method: 'POST' }, { fetchOperationName: 'Log out user' }),
      )

      if (fetchResult.error) {
        console.error('Error logging out of GCS backend', fetchResult.error)
        self.logOutStatus = OperationFailure(fetchResult.error)
      } else {
        self.logOutStatus = OperationSuccess()
        void self._logoutFromOkta() // logout from okta, just in case we couldn't log out after login.
        loginLink.push()
      }
    }),
  }))
